import { Button, Form, Input, InputNumber, Select } from 'antd';
import { useEffect } from 'react';
import ApiCall from 'apps/nhsv/apiCalls/nhsv-webhooks';
import { API_BASE_URL } from 'config/env-config';
import { useFetchApiList } from 'hooks/api';
import { formItemLayout, tailFormItemLayout } from 'utils/formConfig';

const ItemForm = ({
  form,
  submiting,
  onFinish,
  initialValues = {},
  name,
  isCreate = false,
}) => {
  const { data: events, load: loadEvents } = useFetchApiList(ApiCall.events, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (isCreate) loadEvents({});
  }, [isCreate]);

  // Each NHSV event is handled by its own endpoint on the investcore API;
  // the events API returns that endpoint as callback_path, so selecting an
  // event pre-fills the matching target URL.
  const onEventChange = (eventId: number) => {
    const event = (events || []).find((ev: any) => ev.id === eventId);
    if (event?.callback_path) {
      form.setFieldValue('target_url', `${API_BASE_URL}${event.callback_path}`);
    }
  };

  return (
    <Form
      {...formItemLayout}
      form={form}
      name={name || 'nhsv-webhook-form'}
      onFinish={onFinish}
      initialValues={{
        timeout_ms: 5000,
        retry_policy_max_bursts: 3,
        retry_policy_attempts_per_burst: 5,
        retry_policy_cooldown_minutes: 30,
        ...initialValues,
      }}
      scrollToFirstError
    >
      {isCreate && (
        <Form.Item
          name="event_id"
          label="Event"
          rules={[{ required: true, message: 'Please select an event' }]}
        >
          <Select placeholder="Select event type" onChange={onEventChange}>
            {(events || []).map((ev: any) => (
              <Select.Option key={ev.id} value={ev.id}>
                {ev.event_code} — {ev.description}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        name="target_url"
        label="Target URL"
        rules={[
          { required: true, message: 'Target URL is required' },
          { type: 'url', message: 'Must be a valid URL' },
        ]}
        extra="The endpoint URL where NHSV sends this event; pre-filled with the event's dedicated handler"
      >
        <Input placeholder="https://api.stag.vn/api/webhooks/nhsv/contract-status" />
      </Form.Item>

      <Form.Item
        name="timeout_ms"
        label="Timeout (ms)"
        extra="Max wait time in milliseconds for a response from the target URL"
      >
        <InputNumber min={1000} max={30000} step={1000} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item label="Retry Policy — Max Bursts" name="retry_policy_max_bursts" extra="Max retry cycles">
        <InputNumber min={0} max={10} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item label="Retry Policy — Attempts/Burst" name="retry_policy_attempts_per_burst" extra="Attempts within each burst">
        <InputNumber min={1} max={10} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item label="Retry Policy — Cooldown (min)" name="retry_policy_cooldown_minutes" extra="Minutes between bursts">
        <InputNumber min={0} max={120} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit" loading={submiting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ItemForm;
